/* eslint-disable react/no-string-refs */
import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import _get from 'lodash/get';
import clsx from 'clsx';
import clipboard from 'clipboard-js';
import { ReactComponent as EmailIcon } from '@lifesize/ux-assets/vectors/svgIcons/email.svg';
import { ReactComponent as HashtagIcon } from '@lifesize/ux-assets/vectors/svgIcons/hashtag.svg';
import { ReactComponent as InformationIcon } from '@lifesize/ux-assets/vectors/svgIcons/information-outline.svg';
import { ReactComponent as LinkIcon } from '@lifesize/ux-assets/vectors/svgIcons/link-alt.svg';
import { ReactComponent as LockIcon } from '@lifesize/ux-assets/vectors/svgIcons/lock.svg';
import { ReactComponent as PhoneIcon } from '@lifesize/ux-assets/vectors/svgIcons/phone.svg';
import StartingCall from 'components/StartingCallModal/StartingCallModal';
import AddParticipantSearch from 'containers/AddParticipantDrawer/AddParticipantSearchContainer';
import lsConfig from 'utils/global-config';
import logger from 'utils/logger';
import { openNewTab } from 'utils/UrlUtil';
import FileCopyButton from './FileCopyButton';
import FileCopiedIndicator from './FileCopiedIndicator';
import classes from './AddParticipantDrawer.module.scss';

const messages = defineMessages({
  addParticipant: { id: 'components.addParticipant.title', defaultMessage: 'Add a Participant' }
});
let participantSuccessMessageTimeout = null;

const CopyItems = {
  InviteURL: 'inviteURL',
  DialString: 'dialString',
  Pin: 'pin',
  SipUri: 'sipUri',
  PhoneNumber: 'phoneNumber'
};
class AddParticipantDrawer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.fillSectionRef = React.createRef();
    this.state = {
      addParticipantInvitee: false,
      copiedItem: '',
      detailsAreCopied: false,
      showAllCopyItems: false
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      !this.props.callingError &&
      this.props.invitee &&
      !nextProps.invitee &&
      (nextProps.isGalaxyCall || nextProps.participantAdded)
    ) {
      this.setState({
        addParticipantInvitee: this.props.invitee,
        copiedItem: '',
        detailsAreCopied: false,
        showAllCopyItems: false
      });
      participantSuccessMessageTimeout = setTimeout(() => {
        this.setState({ addParticipantInvitee: false });
        this.props.clearSelectedCall();
      }, 3000);
    }
    if (!this.props.isVisible) {
      this.setState({
        copiedItem: '',
        detailsAreCopied: false,
        showAllCopyItems: false
      });
    }
    if (!this.props.isVisible && nextProps.isVisible) {
      window.addEventListener('keydown', this.handleKeyDown);
    } else if (this.props.isVisible && !nextProps.isVisible) {
      window.removeEventListener('keydown', this.handleKeyDown);
    }
    if (nextProps.meetingInfo !== this.props.meetingInfo && this.state.showAllCopyItems) {
      this.setState({
        showAllCopyItems: false
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    if (this.props.invitee) {
      this.props.endOutgoingCall(this.props.invitee);
    }
    clearTimeout(participantSuccessMessageTimeout);
  }

  getContactType = (contact = {}) => {
    if (!contact) return 'manual';
    if (contact.isRoomSystem) return 'roomSystem';
    if (contact.isUser) return 'user';
    if (contact.isManual) return 'manual';
    return 'user';
  };

  handleEmailClick = async () => {
    try {
      const mailto = await this.props.getInviteMailLinkForExtension();
      // Create and click email link
      const element = document.createElement('a');
      element.setAttribute('href', mailto);
      if (!this.props.isDesktop) {
        element.setAttribute('target', '_blank');
      }
      element.style.display = 'none';
      document.body.appendChild(element);

      lsConfig.skipTimeOnBeforeUnload(1000);
      element.click();

      document.body.removeChild(element);
    } catch (err) {
      logger.error(`Unable to create mailto link: ${err}`);
    }
  };

  handleKeyDown = (evt) => {
    if (this.props.isVisible && evt.which === 13 && this.props.contact?.name) {
      evt.preventDefault();
      this.props.call(this.props.contact);
    }
  };

  handleSeeMore = () => {
    this.setState({ showAllCopyItems: true });
  };

  cancelCall = () => {
    this.props.clearOutgoingCall(this.props.invitee);
  };

  redialCall = () => {
    this.props.call(this.props.invitee);
  };

  handleItemCopy = (data, copyItemKey) => {
    clipboard.copy({
      'text/plain': data
    });
    this.setState({
      copiedItem: copyItemKey
    });
    // reset copy state after 2 seconds.
    setTimeout(() => {
      this.setState({
        copiedItem: ''
      });
    }, 2000);
  };

  copyCallDetails = async () => {
    try {
      const invitation = await this.props.getInviteForExtension();
      this.handleItemCopy(invitation.body, 'detailsAreCopied');
      clipboard.copy({
        'text/plain': invitation.body
      });
      this.setState({
        detailsAreCopied: true
      });
      // reset copy state after 2 seconds.
      setTimeout(() => {
        this.setState({
          detailsAreCopied: false
        });
      }, 2000);
    } catch (err) {
      logger.error(`Unable to copy invitation: ${err}`);
    }
  };

  renderCallButton() {
    if (this.props.contact?.name) {
      return (
        <section className={clsx(classes.buttonSection, classes.fill)}>
          <button
            className={classes.primaryButton}
            disabled={!this.props.canAddParticipant}
            onClick={() => {
              this.props.call(this.props.contact);
            }}
            data-testid="add-participant-call-btn"
          >
            <FormattedMessage id="components.addParticipant.call" defaultMessage="Call" />
          </button>
        </section>
      );
    } else if (this.props.isFocused || !!this.props.searchString) {
      return <section ref={this.fillSectionRef} className={classes.fill} />;
    }

    return '';
  }

  render() {
    const {
      additionalNumbersURL,
      callType,
      calling,
      canInviteFromDirectory,
      contact,
      intl,
      invitee,
      isCrossGroup,
      isFocused,
      isWaitingRoomEnabledByModerator,
      isVisible,
      meetingInfo,
      pstnNumbers,
      searchString
    } = this.props;
    const remoteContact = Object.keys(contact || {}).length ? contact : _get(invitee, 'contact');
    if (this.state.addParticipantInvitee) {
      const addParticipantInvitee = this.state.addParticipantInvitee;
      if (!isVisible) return null;
      return (
        <StartingCall
          callName={_get(addParticipantInvitee, 'contact.name') || _get(addParticipantInvitee, 'dialString')}
          callDialString={_get(addParticipantInvitee, 'dialString')}
          callStatus={'success'} // temporary: for QA of WEB-1961
          callType={this.getContactType(this.props.invitationContact)}
          cancelCall={this.cancelCall}
          clearCall={this.cancelCall}
          intl={intl}
          redialCall={this.redialCall}
          callTitle={intl.formatMessage(messages.addParticipant)}
          callingError={false}
          drawerCall
        />
      );
    }

    if (contact && calling && invitee && invitee.dialString) {
      if (!isVisible) return null;
      return (
        <StartingCall
          callName={_get(this.props.invitationContact, 'name') || _get(invitee, 'dialString')}
          callDialString={_get(invitee, 'dialString')}
          callStatus={callType} // temporary: for QA of WEB-1961
          callType={this.getContactType(remoteContact)}
          cancelCall={this.cancelCall}
          intl={intl}
          redialCall={this.redialCall}
          callTitle={intl.formatMessage(messages.addParticipant)}
          callingError={this.props.callingError}
          drawerCall
          clearCall={() => {
            this.props.clearOutgoingCall(invitee);
          }}
          callFailureTimeout={10000}
        />
      );
    }

    const getShowMore = (isCrossGroup) => {
      if (isCrossGroup) return <></>;
      return (
        <div className={classes.copyItem}>
          <a className={classes.seeMore} onClick={this.handleSeeMore}>
            <FormattedMessage id="components.inCallDrawer.sectionInviteGuest.seeMore" defaultMessage="see more" />
          </a>
        </div>
      );
    };

    return (
      <div className={clsx(classes.container, !isVisible && classes.hiddenDrawer)}>
        <header className={classes.drawerTitle}>{intl.formatMessage(messages.addParticipant)}</header>
        <>
          {isWaitingRoomEnabledByModerator && (
            <section className={classes.lockedMessage}>
              <FormattedMessage
                id="components.inCall.waitingRoom.enabled"
                defaultMessage="Waiting room has been enabled for this meeting"
              />
            </section>
          )}
          {canInviteFromDirectory && (
            <section>
              <header className={classes.subtitle}>
                <FormattedMessage
                  id="components.inCallDrawer.sectionInviteFromDirectory"
                  defaultMessage="Invite from Directory"
                />
              </header>
              <AddParticipantSearch contact={contact} fillSectionRef={this.fillSectionRef}/>
            </section>
          )}
          {!contact && !searchString && !isFocused && (
            <section className={classes.scrollable}>
              <header className={classes.subtitle}>
                <FormattedMessage id="components.inCallDrawer.sectionInviteGuest" defaultMessage="Invite Guest" />
              </header>
              <div className={classes.sectionBody}>
                <div className={classes.copyItem}>
                  <label>
                    <FormattedMessage id="components.contactDetails.meetingLink" defaultMessage="Meeting Link" />
                  </label>
                  <div className={classes.group}>
                    <div className={classes.data}>
                      <LinkIcon className={classes.icon} />
                      <div className={classes.text}>{meetingInfo.inviteURL}</div>
                    </div>
                    {this.state.copiedItem === CopyItems.InviteURL ? (
                      <FileCopiedIndicator />
                    ) : (
                      <FileCopyButton
                        handleClick={() => {
                          this.handleItemCopy(_get(meetingInfo, CopyItems.InviteURL), CopyItems.InviteURL);
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className={classes.copyItem}>
                  <label>
                    <FormattedMessage id="components.contactDetails.extension" defaultMessage="Extension" />
                  </label>
                  <div className={classes.group}>
                    <div className={classes.data}>
                      <HashtagIcon className={classes.icon} />
                      <div className={classes.text}>{meetingInfo.dialString}</div>
                    </div>
                    {this.state.copiedItem === CopyItems.DialString ? (
                      <FileCopiedIndicator />
                    ) : (
                      <FileCopyButton
                        handleClick={() => {
                          this.handleItemCopy(_get(meetingInfo, CopyItems.DialString), CopyItems.DialString);
                        }}
                      />
                    )}
                  </div>
                </div>
                {!!meetingInfo.pin && (
                  <div className={classes.copyItem}>
                    <label>
                      <FormattedMessage id="components.contactDetails.passcode" defaultMessage="Passcode" />
                    </label>
                    <div className={classes.group}>
                      <div className={classes.data}>
                        <LockIcon className={classes.icon} />
                        <div className={classes.text}>{meetingInfo.pin}</div>
                      </div>
                      {this.state.copiedItem === CopyItems.Pin ? (
                        <FileCopiedIndicator />
                      ) : (
                        <FileCopyButton
                          handleClick={() => {
                            this.handleItemCopy(_get(meetingInfo, CopyItems.Pin), CopyItems.Pin);
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
                {this.state.showAllCopyItems ? (
                  <>
                    <div className={classes.copyItem}>
                      <label>
                        <FormattedMessage id="components.contactDetails.videoAddress" defaultMessage="Video Address" />
                      </label>
                      <div className={classes.group}>
                        <div className={classes.data}>
                          <EmailIcon className={classes.icon} />
                          <div className={classes.text}>{meetingInfo.sipUri || _get(meetingInfo, 'video')}</div>
                        </div>
                        {this.state.copiedItem === CopyItems.SipUri ? (
                          <FileCopiedIndicator />
                        ) : (
                          <FileCopyButton
                            handleClick={() => {
                              this.handleItemCopy(
                                _get(meetingInfo, CopyItems.SipUri) || _get(meetingInfo, 'video'),
                                CopyItems.SipUri
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                    {!!pstnNumbers.length && (
                      <div className={classes.copyItem}>
                        <label>
                          <FormattedMessage id="components.contactDetails.phone" defaultMessage="Phone" />
                        </label>
                        {pstnNumbers.map((phone, index) => (
                          <div key={`phone${index}`} className={classes.group}>
                            <div className={classes.data}>
                              <PhoneIcon className={classes.icon} />
                              <div className={classes.text}>
                                {_get(phone, 'countryCode')}:{' '}
                                <span className={classes.phoneNumber}>{_get(phone, 'phoneNumber')}</span>
                              </div>
                            </div>
                            {this.state.copiedItem === `${CopyItems.PhoneNumber}${index}` ? (
                              <FileCopiedIndicator />
                            ) : (
                              <FileCopyButton
                                handleClick={() => {
                                  this.handleItemCopy(
                                    _get(phone, CopyItems.PhoneNumber),
                                    `${CopyItems.PhoneNumber}${index}`
                                  );
                                }}
                              />
                            )}
                          </div>
                        ))}
                        {additionalNumbersURL && (
                          <a
                            className={classes.additionalNumbers}
                            onClick={() => openNewTab(additionalNumbersURL, 'AdditionalNumbers')}
                            data-testid="open-additional-numbers"
                          >
                            <FormattedMessage
                              id="components.contactDetails.additionalNumbers"
                              defaultMessage="Additional numbers"
                            />
                          </a>
                        )}
                      </div>
                    )}
                    <div className={classes.buttonSection}>
                      <button
                        type="button"
                        className={clsx(classes.primaryButton, this.state.detailsAreCopied && classes.success)}
                        data-testid="Copy All details"
                        onClick={this.copyCallDetails}
                      >
                        <FormattedMessage
                          id="components.inCallDrawer.sectionInviteGuest.copyCallDetails"
                          defaultMessage="Copy All Details"
                        />
                      </button>
                      <button
                        className={classes.secondaryButton}
                        data-testid="invite-by-email"
                        onClick={this.handleEmailClick}
                      >
                        <FormattedMessage
                          id="components.inCall.callDetailsDrawer.inviteByEmail"
                          defaultMessage="Invite By Email"
                        />
                      </button>
                    </div>
                  </>
                ) : (
                  getShowMore(isCrossGroup)
                )}
              </div>
            </section>
          )}
          {this.renderCallButton()}
          <section className={classes.footerButtons}>
            <button
              data-testid="btnOtherWaysToCall"
              onClick={() => openNewTab(this.props.otherWaysToJoinURL, 'OtherWaysToJoin')}
            >
              <InformationIcon data-testid="informationIcon" />
              <span>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.toggle"
                  defaultMessage="How to call someone not in your directory"
                />
              </span>
            </button>
          </section>
        </>
      </div>
    );
  }
}

AddParticipantDrawer.propTypes = {
  additionalNumbersURL: PT.string,
  call: PT.func.isRequired,
  callType: PT.string,
  calling: PT.bool,
  callingError: PT.bool,
  canInviteFromDirectory: PT.bool,
  canAddParticipant: PT.bool,
  clearOutgoingCall: PT.func,
  clearSelectedCall: PT.func,
  contact: PT.object,
  endOutgoingCall: PT.func,
  getInviteForExtension: PT.func,
  getInviteMailLinkForExtension: PT.func,
  intl: PT.object,
  invitationContact: PT.object,
  invitee: PT.object,
  isCrossGroup: PT.bool,
  isDesktop: PT.bool,
  isGalaxyCall: PT.bool,
  isWaitingRoomEnabledByModerator: PT.bool,
  isVisible: PT.bool,
  manualDial: PT.bool,
  meetingInfo: PT.object.isRequired,
  otherWaysToJoinURL: PT.string,
  participantAdded: PT.any,
  pstnNumbers: PT.array.isRequired,
  searchString: PT.string,
  isUserModerator: PT.bool
};

export default injectIntl(AddParticipantDrawer);
